import React, { useMemo, useState, useRef } from 'react';
import Accordion from '../../../../core/componentes/comuns/Accordion';
import { ComponenteGraficoPrognostico } from '../peticao/ComponenteGraficoPrognostico';

import { ComponenteListaDocumentos } from '../historico/ComponenteListaDocumentos';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import jsPDF from 'jspdf';
import { FileText, Copy, Download, Plus, TrendingUp, Scale, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import type { ResultadoCriacaoPeticao, PeticaoInicialGerada, AnalisePreditiva, ProximosPassosEstrategicos } from '../../tipos/tiposCriacaoPeticao';
import type { Prognostico, ProximosPassos } from '../../tipos/tiposPeticao';
import type { TipoDocumento } from '../../tipos/tiposDocumentos';

interface PropsComponenteResultadosAnalise {
  resultado: ResultadoCriacaoPeticao;
  onNovaAnalise?: () => void;
  onEditarPeticao?: (peticaoId: string) => void;
  perspectiva?: 'autor' | 'reu';
}

const CardIndicador = ({ titulo, valor, icone, cor, descricao }: { titulo: string; valor: string | number; icone: React.ReactNode; cor: string; descricao: string }) => {
  const colorClasses: Record<string, { bg: string; border: string; textTitle: string; textValue: string; textDesc: string }> = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', textTitle: 'text-emerald-900', textValue: 'text-emerald-700', textDesc: 'text-emerald-600' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', textTitle: 'text-blue-900', textValue: 'text-blue-700', textDesc: 'text-blue-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', textTitle: 'text-amber-900', textValue: 'text-amber-700', textDesc: 'text-amber-600' },
    red: { bg: 'bg-red-50', border: 'border-red-100', textTitle: 'text-red-900', textValue: 'text-red-700', textDesc: 'text-red-600' },
  };

  const colors = colorClasses[cor] || colorClasses.blue;

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icone}
          <span className={`text-sm font-medium ${colors.textTitle}`}>{titulo}</span>
        </div>
      </div>
      <div className={`mt-2 text-2xl font-bold ${colors.textValue}`}>{valor}</div>
      <p className={`mt-1 text-xs ${colors.textDesc}`}>{descricao}</p>
    </div>
  );
};

export const ComponenteResultadosAnalise: React.FC<PropsComponenteResultadosAnalise> = ({ resultado, onNovaAnalise, onEditarPeticao, perspectiva = 'reu' }) => {
  const [selectedEstrategia, setSelectedEstrategia] = useState<string>('conservadora');
  // When resultado arrives, select first peticao if available
  React.useEffect(() => {
    if (resultado && resultado.peticoesGeradas && resultado.peticoesGeradas.length) {
      setSelectedEstrategia(resultado.peticoesGeradas[0].estrategia);
    }
  }, [resultado]);

  // If the resultado is not available, we will render skeletons later after hooks

  // Utility: find peticao by estrategia
  const peticaoSelecionada = useMemo<PeticaoInicialGerada | undefined>(() => {
    return resultado?.peticoesGeradas?.find((p) => p.estrategia === selectedEstrategia);
  }, [resultado, selectedEstrategia]);

  // Map AnalisePreditiva -> Prognostico (used by ComponenteGraficoPrognostico)
  const prognosticoConvertido: Prognostico = useMemo(() => {
    const ap: AnalisePreditiva | undefined = resultado?.analisePreditiva;
    if (!ap) {
      return {
        cenario_mais_provavel: '—',
        cenarios: [],
        recomendacao_geral: '—',
      } as Prognostico;
    }

    // Ajustar descrições baseadas na perspectiva
    const isAutor = perspectiva === 'autor';
    const sufixo = isAutor ? '(Autor)' : '(Defesa)';

    return {
      cenario_mais_provavel: ap.probabilidadeSucesso ? 'Vitória Total' : '—',
      cenarios: [
        {
          tipo: 'vitoria_total',
          descricao: `Vitória total ${sufixo}`,
          probabilidade_percentual: ap.probabilidadeSucesso ?? 0,
          valores_estimados: {
            receber: ap.valorEstimadoMax ?? 0,
            pagar: ap.valorEstimadoMin ?? 0,
          },
        },
        {
          tipo: 'vitoria_parcial',
          descricao: `Vitória parcial ${sufixo}`,
          probabilidade_percentual: ap.probabilidadeDerrotaParcial ?? 0,
          valores_estimados: { receber: 0, pagar: 0 },
        },
        {
          tipo: 'acordo',
          descricao: 'Acordo',
          probabilidade_percentual: ap.probabilidadeAcordo ?? 0,
          valores_estimados: { receber: 0, pagar: 0 },
        },
        {
          tipo: 'derrota',
          descricao: `Derrota ${sufixo}`,
          probabilidade_percentual: ap.probabilidadeDerrota ?? 0,
          valores_estimados: { receber: 0, pagar: 0 },
        },
      ],
      recomendacao_geral: ap.fundamentos && Array.isArray(ap.fundamentos) && ap.fundamentos.length ? ap.fundamentos.join('; ') : '—',
      fatores_criticos: Array.isArray(ap.riscosIdentificados) ? ap.riscosIdentificados : [],
    };
  }, [resultado, perspectiva]);

  // Map ProximosPassosEstrategicos -> ProximosPassos
  const proximosPassosConvertidos: ProximosPassos = useMemo(() => {
    const p: ProximosPassosEstrategicos | undefined = resultado?.proximosPassos;
    if (!p) {
      return { estrategia_recomendada: '—', passos: [] } as ProximosPassos;
    }
    return {
      estrategia_recomendada: Array.isArray(p.recomendacoesGerais) ? (p.recomendacoesGerais[0] ?? '—') : '—',
      passos: Array.isArray(p.passos) ? p.passos.map((pr) => ({
        numero: pr.ordem || pr.ordem || 0,
        descricao: pr.descricao || '',
        prazo_estimado: pr.prazoSugerido || pr.prazoSugerido || '',
        responsavel: undefined,
        documentos_necessarios: [],
      })) : [],
      alertas: undefined,
      caminhos_alternativos: undefined,
    };
  }, [resultado]);

  // Ref for the petition content to be exported
  const peticaoRef = useRef<HTMLDivElement>(null);

  // PDF export for a single petition using html2canvas/jsPDF
  const exportarPeticaoPDF = async (titulo: string) => {
    if (!peticaoRef.current) return;

    const doc = new jsPDF({
      unit: 'pt',
      format: 'a4',
      orientation: 'portrait'
    });

    // Scale factor to fit A4 width (595.28pt) with margins
    // A4 width is ~595pt. We want some margin.
    // html method handles this, but we can tweak options.

    try {
      await doc.html(peticaoRef.current, {
        callback: (doc) => {
          const nomeArquivo = `${titulo.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.pdf`;
          doc.save(nomeArquivo);
        },
        x: 40,
        y: 40,
        width: 515, // 595 - 40 - 40
        windowWidth: 800, // Width of the window to render the HTML
        autoPaging: 'text',
        margin: [40, 40, 40, 40]
      });
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
    }
  };



  // Export all - combined report (kept as text for now or could be improved later)
  const exportarTudoPDF = () => {
    const doc = new jsPDF();
    const margem = 15;
    const larguraUtil = doc.internal.pageSize.width - margem * 2;
    let y = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Relatório Completo - Resultados da Análise', margem, y);
    y += 10;
    // For brevity, keeping the existing text-based logic for "Exportar Tudo" 
    // but the user specifically asked for the petition display improvement.

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margem, y);
    y += 12;

    // Analise Preditiva resumo
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Análise Preditiva', margem, y);
    y += 8;

    const resumoPreditivo = `Probabilidade de Sucesso: ${resultado.analisePreditiva.probabilidadeSucesso}%\nProbabilidade de Derrota: ${resultado.analisePreditiva.probabilidadeDerrota}%\nProbabilidade de Acordo: ${resultado.analisePreditiva.probabilidadeAcordo}%\nProbabilidade de Derrota Parcial: ${resultado.analisePreditiva.probabilidadeDerrotaParcial}%`;
    doc.setFontSize(11);
    const linhasP = doc.splitTextToSize(resumoPreditivo, larguraUtil);
    linhasP.forEach((linha: string) => {
      if (y > doc.internal.pageSize.height - 20) {
        doc.addPage();
        y = 20;
      }
      doc.text(linha, margem, y);
      y += 7;
    });
    y += 8;

    // Vincular próximos passos
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Próximos Passos Estratégicos', margem, y);
    y += 8;

    if (resultado.proximosPassos.passos && resultado.proximosPassos.passos.length) {
      resultado.proximosPassos.passos.forEach((passo) => {
        const textoPasso = `${passo.ordem || 0}. ${passo.titulo || passo.descricao} - ${passo.prazoSugerido || ''}`;
        const linhasPasso = doc.splitTextToSize(textoPasso, larguraUtil);
        linhasPasso.forEach((l: string) => {
          if (y > doc.internal.pageSize.height - 20) {
            doc.addPage();
            y = 20;
          }
          doc.text(l, margem, y);
          y += 7;
        });
        y += 3;
      });
    }

    y += 8;

    // Petições generated summary
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Petições Geradas', margem, y);
    y += 8;

    resultado.peticoesGeradas.forEach((peticao) => {
      const header = `${peticao.estrategia} - ${peticao.titulo} - Probabilidade: ${peticao.probabilidadeSucesso}%`;
      const linhasHeader = doc.splitTextToSize(header, larguraUtil);
      linhasHeader.forEach((l: string) => {
        if (y > doc.internal.pageSize.height - 20) {
          doc.addPage();
          y = 20;
        }
        doc.text(l, margem, y);
        y += 7;
      });

      const linhasConteudo = doc.splitTextToSize(peticao.conteudoCompleto || '', larguraUtil);
      linhasConteudo.forEach((l: string) => {
        if (y > doc.internal.pageSize.height - 20) {
          doc.addPage();
          y = 20;
        }
        doc.text(l, margem, y);
        y += 7;
      });
      y += 10;
    });

    const nomeArquivo = `relatorio_resultados_${Date.now()}.pdf`;
    doc.save(nomeArquivo);
  };

  const copiarParaClipboard = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  // State para mostrar mais/menos pontos-chave


  if (!resultado) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-48 rounded bg-gray-100" />
          <div className="h-48 rounded bg-gray-100" />
        </div>
        <div className="h-10 w-40 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  // Helper to strip markdown code blocks if present
  const limparMarkdown = (texto: string) => {
    if (!texto) return '';
    // Remove starting ```markdown or ```
    let limpo = texto.replace(/^```(?:markdown)?\s*/i, '');
    // Remove ending ```
    limpo = limpo.replace(/\s*```$/, '');
    return limpo;
  };

  return (
    <div className="space-y-6">
      {/* Header: Ações Globais */}
      <div className="flex items-center justify-end gap-2">
        {onNovaAnalise && (
          <button
            onClick={onNovaAnalise}
            className="inline-flex items-center gap-2 rounded border border-primary-600 bg-white px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50"
            title="Iniciar nova análise"
          >
            <Plus className="size-4" />
            Nova Análise
          </button>
        )}
        <button
          onClick={exportarTudoPDF}
          className="btn-primary flex items-center gap-2"
          title="Exportar tudo como PDF"
        >
          <Download className="size-4" />
          Exportar Tudo
        </button>
      </div>

      {/* Accordion 1: Análise Preditiva */}
      <Accordion titulo="Análise Preditiva" descricao="Probabilidades e métricas chave" defaultOpen>
        <ComponenteGraficoPrognostico dados={prognosticoConvertido} perspectiva={perspectiva} />

        {/* Cards: Probabilidade Sucesso, Jurisprudência Favorável, Valor Estimado, Risco Geral */}
        <div className="grid gap-4 md:grid-cols-2">
          <CardIndicador
            titulo="Probabilidade de Sucesso"
            valor={`${resultado.analisePreditiva.probabilidadeSucesso}%`}
            icone={<TrendingUp className="size-5 text-emerald-600" />}
            cor="emerald"
            descricao="Baseado em casos similares e jurisprudência recente"
          />
          <CardIndicador
            titulo="Jurisprudência Favorável"
            valor={`${resultado.analisePreditiva.jurisprudenciaFavoravel?.toFixed(1) ?? 0}%`}
            icone={<Scale className="size-5 text-blue-600" />}
            cor="blue"
            descricao="Percentual de decisões favoráveis encontradas"
          />
          <CardIndicador
            titulo="Valor Estimado (Médio)"
            valor={`R$ ${((resultado.analisePreditiva.valorEstimadoMin || 0) + (resultado.analisePreditiva.valorEstimadoMax || 0)) / 2}`}
            icone={<DollarSign className="size-5 text-amber-600" />}
            cor="amber"
            descricao={`Faixa: R$ ${resultado.analisePreditiva.valorEstimadoMin} - R$ ${resultado.analisePreditiva.valorEstimadoMax}`}
          />
          <CardIndicador
            titulo="Risco Geral"
            valor={(resultado.analisePreditiva?.probabilidadeDerrota ?? 0) > 50 ? 'Alto' : (resultado.analisePreditiva?.probabilidadeDerrota ?? 0) > 20 ? 'Médio' : 'Baixo'}
            icone={<AlertTriangle className="size-5 text-red-600" />}
            cor="red"
            descricao={`Probabilidade de derrota: ${resultado.analisePreditiva?.probabilidadeDerrota ?? 0}%`}
          />
        </div>

        {/* Pontos Fortes e Fracos */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-emerald-50 p-4">
            <h4 className="mb-2 flex items-center gap-2 font-semibold text-emerald-800">
              <CheckCircle className="size-4" />
              Pontos Fortes
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-emerald-700">
              {resultado.analisePreditiva.pontosFortes?.map((p, i) => (
                <li key={i}>{p}</li>
              )) || <li>Nenhum ponto forte destacado.</li>}
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-red-50 p-4">
            <h4 className="mb-2 flex items-center gap-2 font-semibold text-red-800">
              <AlertTriangle className="size-4" />
              Pontos Fracos / Riscos
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-red-700">
              {resultado.analisePreditiva.pontosFracos?.map((p, i) => (
                <li key={i}>{p}</li>
              )) || <li>Nenhum ponto fraco destacado.</li>}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Accordion 2: Estratégia Processual */}
      <Accordion titulo="Estratégia Processual" descricao="Recomendações e próximos passos">
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-4">
            <h4 className="mb-2 font-semibold text-slate-800">Estratégia Recomendada</h4>
            <p className="text-sm text-slate-600">{proximosPassosConvertidos.estrategia_recomendada}</p>
          </div>

          <div>
            <h4 className="mb-3 font-semibold text-slate-800">Próximos Passos Sugeridos</h4>
            <div className="space-y-3">
              {proximosPassosConvertidos.passos.map((passo, idx) => (
                <div key={idx} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                    {passo.numero}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{passo.descricao}</p>
                    {passo.prazo_estimado && (
                      <p className="mt-1 text-xs text-slate-500">Prazo sugerido: {passo.prazo_estimado}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Accordion>

      {/* Accordion 3: Petições Geradas */}
      <Accordion titulo="Petições Geradas" descricao="Minutas prontas para uso" defaultOpen>
        <div className="space-y-4">
          {/* Seletor de Estratégia */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {resultado.peticoesGeradas.map((peticao) => (
              <button
                key={peticao.estrategia}
                onClick={() => setSelectedEstrategia(peticao.estrategia)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${selectedEstrategia === peticao.estrategia
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {peticao.estrategia.charAt(0).toUpperCase() + peticao.estrategia.slice(1)}
              </button>
            ))}
          </div>

          {peticaoSelecionada ? (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h3 className="font-semibold text-slate-800">{peticaoSelecionada.titulo}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => copiarParaClipboard(peticaoSelecionada.conteudoCompleto)}
                    className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                    title="Copiar texto"
                  >
                    <Copy className="size-4" />
                  </button>
                  <button
                    onClick={() => exportarPeticaoPDF(peticaoSelecionada.titulo)}
                    className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                    title="Baixar PDF"
                  >
                    <Download className="size-4" />
                  </button>
                  {onEditarPeticao && (
                    <button
                      onClick={() => onEditarPeticao(peticaoSelecionada.id || '')}
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                      title="Editar no editor completo"
                    >
                      <FileText className="size-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Visualização */}
              <div className="bg-slate-100 p-4 md:p-8">
                <div
                  ref={peticaoRef}
                  className="mx-auto min-h-[29.7cm] w-full max-w-[21cm] bg-white p-[2.5cm] text-justify font-serif text-slate-900 shadow-lg"
                  style={{ fontSize: '12pt', lineHeight: '1.5' }}
                >
                  <div className="prose prose-slate prose-p:indent-8 prose-headings:font-bold prose-h1:text-center prose-h1:uppercase prose-h1:text-lg prose-h2:uppercase prose-h2:text-base prose-p:text-justify prose-p:leading-relaxed max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {limparMarkdown(peticaoSelecionada.conteudoCompleto)}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">
              Nenhuma petição encontrada para esta estratégia.
            </div>
          )}
        </div>
      </Accordion>

      {/* Accordion 4: Documentos Anexados */}
      <Accordion titulo="Documentos Anexados" descricao="Arquivos enviados e anexados à petição" defaultOpen={false}>
        <ComponenteListaDocumentos
          documentos={resultado.documentosAnexados.map((d) => ({
            idDocumento: d.id,
            nomeArquivo: d.nome,
            tipoDocumento: d.tipo as TipoDocumento,
            tamanhoEmBytes: d.tamanho,
            dataHoraUpload: d.dataUpload,
            numeroChunks: undefined,
          }))}
          paginacao={{ paginaAtual: 1, itensPorPagina: resultado.documentosAnexados.length || 10, totalItens: resultado.documentosAnexados.length, totalPaginas: 1 }}
          ordenacao={{ campo: 'dataHoraUpload', direcao: 'desc' }}
          onPaginaMudar={() => { /* Não necessário aqui, de exemplo */ }}
          onAcao={() => { /* Ações: visualizar, deletar, etc. (não habilitado) */ }}
          carregando={false}
        />
      </Accordion>

      {/* Accordion 5: Contexto Fornecido */}
      <Accordion titulo="Contexto Fornecido" descricao="Dados e informações iniciais" defaultOpen={false}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Descrição do Caso</h4>
            <p className="text-sm text-gray-700">{resultado.contextoInicial.descricaoCaso}</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Parte Autora / Ré</h4>
            <p className="text-sm text-gray-700">{resultado.contextoInicial.parteAutora} / {resultado.contextoInicial.parteRe ?? '—'}</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Pedidos Pretendidos</h4>
            <p className="text-sm text-gray-700">{resultado.contextoInicial.pedidosPretendidos ?? '—'}</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Informações Adicionais</h4>
            <div className="text-sm text-gray-700">
              {resultado.informacoesAdicionaisFornecidas && Object.keys(resultado.informacoesAdicionaisFornecidas).length > 0 ? (
                <ul className="space-y-1">
                  {Object.entries(resultado.informacoesAdicionaisFornecidas).map(([k, v]) => (
                    <li key={k}><strong className="text-gray-600">{k}: </strong>{String(v)}</li>
                  ))}
                </ul>
              ) : (
                <p>—</p>
              )}
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default ComponenteResultadosAnalise;
